/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login page if user is not authenticated.
 * Shows loading spinner while checking auth state.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import MfaChallengeGuard from './MfaChallengeGuard';
import { MFA_SETUP_PATH } from '../../services/mfaChallenge';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerification?: boolean;
  /**
   * Redirect to enrolment unless the backend reports this user as enrolled.
   *
   * `user.mfaEnabled` is read from `GET /api/v1/auth/me`, which returns
   * `users.mfa_enabled` - the same column MFA enforcement gates on. So this
   * flag and the backend requirement now agree by construction.
   *
   * It did not always. The value used to be derived from Firebase's
   * enrolled-factor list, which is empty for every user and always will be:
   * enrolling a TOTP authenticator through /api/v1/mfa creates no Firebase
   * factor, and project-level Firebase MFA is disabled on
   * essay-agent-prod-v1. This flag was therefore unsatisfiable by any action
   * a user could take. If you are reading an older note that says this prop
   * cannot honour a backend requirement, that note is stale.
   *
   * It is still redundant, which is why it has no call sites: the backend's
   * requirement is enforced by the 403 challenge below, which needs no
   * per-route opt-in and is driven by the account flag itself. Prefer that.
   */
  requireMFA?: boolean;
}

export default function ProtectedRoute({
  children,
  requireEmailVerification = false,
  requireMFA = false,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col items-center justify-center">
        <Sparkles className="w-12 h-12 text-purple-600 mb-4" />
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to verification page if email not verified
  if (requireEmailVerification && !user.emailVerified) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  // Opt-in, backend-sourced, and redundant with the challenge guard below.
  // See the note on `requireMFA` above.
  if (requireMFA && !user.mfaEnabled) {
    return <Navigate to={MFA_SETUP_PATH} state={{ from: location }} replace />;
  }

  // The backend's own MFA requirement, shared with /wix-dashboard so the
  // two surfaces cannot drift apart. Needs no per-route opt-in.
  return <MfaChallengeGuard>{children}</MfaChallengeGuard>;
}
